import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, User, Bot, 
  Loader2, LogOut, Circle, Eye, Activity, Compass, Target, 
  Shield, Users, Heart, Star, Plus, Copy, ArrowLeft, 
  Flame, Key, Sparkles, Mountain, TreePine, GitBranch, Waves,
  Anchor, Hand, DoorOpen, Footprints, Fingerprint, SunMedium,
  RefreshCw, Zap, ArrowLeftRight
} from 'lucide-react';
import { auth, db } from './firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously
} from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  setDoc,
  getDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import './App.css';

// ============================================
// CONSTANTS
// ============================================

// Resident definitions with symbol options
const RESIDENTS = [
  {
    id: 'trust',
    name: 'Trust',
    question: 'Am I safe?',
    description: 'The part of you that asks: Am I safe?',
    symbols: [
      { id: 'shield', icon: Shield, name: 'Shield' },
      { id: 'anchor', icon: Anchor, name: 'Anchor' },
      { id: 'flame', icon: Flame, name: 'Flame' },
      { id: 'hand', icon: Hand, name: 'Cupped Hands' }
    ]
  },
  {
    id: 'autonomy',
    name: 'Autonomy',
    question: 'Do I choose this?',
    description: 'The part of you that asks: Do I choose this?',
    symbols: [
      { id: 'key', icon: Key, name: 'Key' },
      { id: 'door', icon: DoorOpen, name: 'Open Door' },
      { id: 'compass', icon: Compass, name: 'Compass' },
      { id: 'footprints', icon: Footprints, name: 'Footprints' }
    ]
  },
  {
    id: 'initiative',
    name: 'Initiative',
    question: 'What can I try?',
    description: 'The part of you that asks: What can I try?',
    symbols: [
      { id: 'spark', icon: Sparkles, name: 'Spark' },
      { id: 'seedling', icon: TreePine, name: 'Seedling' },
      { id: 'target', icon: Target, name: 'Arrow' },
      { id: 'sun', icon: SunMedium, name: 'Torch' }
    ]
  },
  {
    id: 'industry',
    name: 'Industry',
    question: 'What must be done?',
    description: 'The part of you that asks: What must be done?',
    symbols: [
      { id: 'mountain', icon: Mountain, name: 'Mountain' },
      { id: 'hammer', icon: Activity, name: 'Steady Work' },
      { id: 'hands', icon: Hand, name: 'Working Hands' },
      { id: 'cornerstone', icon: Shield, name: 'Cornerstone' }
    ]
  },
  {
    id: 'identity',
    name: 'Identity',
    question: 'Who am I?',
    description: 'The part of you that asks: Who am I in this?',
    symbols: [
      { id: 'mirror', icon: Eye, name: 'Mirror' },
      { id: 'tree', icon: TreePine, name: 'Tree' },
      { id: 'fingerprint', icon: Fingerprint, name: 'Fingerprint' },
      { id: 'mask', icon: User, name: 'Mask' }
    ]
  },
  {
    id: 'intimacy',
    name: 'Intimacy',
    question: 'Can I be truly seen?',
    description: 'The part of you that asks: Can I be truly seen?',
    symbols: [
      { id: 'window', icon: Eye, name: 'Open Window' },
      { id: 'flames', icon: Flame, name: 'Two Flames' },
      { id: 'bridge', icon: GitBranch, name: 'Bridge' },
      { id: 'heart', icon: Heart, name: 'Open Heart' }
    ]
  },
  {
    id: 'generativity',
    name: 'Generativity',
    question: 'What am I building beyond myself?',
    description: 'The part of you that asks: What am I building beyond myself?',
    symbols: [
      { id: 'river', icon: Waves, name: 'River' },
      { id: 'seed', icon: TreePine, name: 'Planted Seed' },
      { id: 'horizon', icon: SunMedium, name: 'Horizon' },
      { id: 'star', icon: Star, name: 'Guiding Star' }
    ]
  }
];

// Color palette for residents
const COLORS = [
  { id: 'red', name: 'Red', value: '#ef4444' },
  { id: 'orange', name: 'Orange', value: '#f97316' },
  { id: 'amber', name: 'Amber', value: '#f59e0b' },
  { id: 'green', name: 'Green', value: '#22c55e' },
  { id: 'teal', name: 'Teal', value: '#14b8a6' },
  { id: 'blue', name: 'Blue', value: '#3b82f6' },
  { id: 'purple', name: 'Purple', value: '#8b5cf6' },
  { id: 'pink', name: 'Pink', value: '#ec4899' }
];

// Practice scenarios - 50 scenarios across 10 life domains
const PRACTICE_SCENARIOS = [
  // ============================================
  // RELATIONSHIPS (5)
  // ============================================
  {
    id: 'the-move',
    title: 'The Move',
    category: 'Relationships',
    summary: {
      what: 'One partner got a dream job offer in another city',
      how: 'The offer came suddenly, requiring a decision within weeks',
      where: 'The future of the relationship and both careers',
      who: 'The partner with the offer and the partner being asked to relocate'
    },
    matter: 'One partner got a job offer in another city. The other has roots here.',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Got the job offer. Excited but torn.' },
      { id: 'b', name: 'Partner B', description: 'Being asked to leave everything. Scared and conflicted.' }
    ]
  },
  {
    id: 'long-distance-crossroads',
    title: 'Long Distance Crossroads',
    category: 'Relationships',
    summary: {
      what: 'Two years of long distance, and someone needs to move',
      how: 'Both have established careers in different cities',
      where: 'Commitment, sacrifice, and whose life gets uprooted',
      who: 'Two partners who love each other but are tired of distance'
    },
    matter: 'The long distance has gone on long enough. Someone has to move, or you both move on.',
    perspectives: [
      { id: 'a', name: 'Alex', description: 'Wants commitment but fears giving up their career.' },
      { id: 'b', name: 'Jordan', description: 'Loves deeply but questions if love is enough.' }
    ]
  },
  {
    id: 'different-timelines',
    title: 'Different Timelines',
    category: 'Relationships',
    summary: {
      what: 'One partner wants kids soon, the other isn\'t ready',
      how: 'The conversation keeps getting postponed until it can\'t be',
      where: 'The future shape of the family and relationship',
      who: 'Partners with different visions of when to start a family'
    },
    matter: 'The clock is ticking differently for each of you. One wants kids now. The other needs more time.',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Feels the biological and emotional urgency.' },
      { id: 'b', name: 'Partner B', description: 'Loves their partner but isn\'t ready for parenthood.' }
    ]
  },
  {
    id: 'the-ex-factor',
    title: 'The Ex Factor',
    category: 'Relationships',
    summary: {
      what: 'A close friendship with an ex is causing tension',
      how: 'The friendship predates the current relationship',
      where: 'Trust, boundaries, and past vs. present',
      who: 'The partner with the ex-friendship and the uncomfortable partner'
    },
    matter: 'One partner maintains a close friendship with an ex. The other can\'t shake the discomfort.',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Values the friendship. Feels trust should be enough.' },
      { id: 'b', name: 'Partner B', description: 'Trusts their partner but not the situation.' }
    ]
  },
  {
    id: 'love-languages-gap',
    title: 'The Love Languages Gap',
    category: 'Relationships',
    summary: {
      what: 'Both feel unloved despite both trying hard',
      how: 'They express love differently and feel unseen',
      where: 'Emotional connection and feeling valued',
      who: 'Two partners who love each other but miss each other\'s signals'
    },
    matter: 'You both feel like you\'re giving everything, yet both feel unseen.',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Shows love through acts. Needs words to feel loved.' },
      { id: 'b', name: 'Partner B', description: 'Shows love through presence. Needs touch to feel loved.' }
    ]
  },

  // ============================================
  // FAMILY (5)
  // ============================================
  {
    id: 'family-care',
    title: 'The Family Care',
    category: 'Family',
    summary: {
      what: 'Aging parent needs more care than one sibling can give',
      how: 'One sibling lives nearby and carries most of the burden',
      where: 'Family duty, fairness, and the parent\'s wellbeing',
      who: 'The nearby sibling and the distant sibling'
    },
    matter: 'Mom needs more care. One sibling is local and exhausted. The other is far away with resources.',
    perspectives: [
      { id: 'a', name: 'Local Sibling', description: 'Exhausted from daily caregiving. Needs help.' },
      { id: 'b', name: 'Distant Sibling', description: 'Wants to help but can\'t be there physically.' }
    ]
  },
  {
    id: 'inheritance-divide',
    title: 'The Inheritance Divide',
    category: 'Family',
    summary: {
      what: 'Parents left unequal inheritances and it surfaced old wounds',
      how: 'The will was read and someone feels slighted',
      where: 'Family bonds, fairness, and decades of history',
      who: 'Siblings processing unequal treatment from beyond the grave'
    },
    matter: 'The will wasn\'t equal. One sibling got more. The other wants to understand why.',
    perspectives: [
      { id: 'a', name: 'Sibling A', description: 'Received more. Feels guilty but also feels it was fair.' },
      { id: 'b', name: 'Sibling B', description: 'Received less. Feels the sting of a lifetime of being second.' }
    ]
  },
  {
    id: 'holiday-tug-of-war',
    title: 'Holiday Tug of War',
    category: 'Family',
    summary: {
      what: 'Both families expect you for the holidays',
      how: 'Years of alternating haven\'t satisfied anyone',
      where: 'Loyalty, tradition, and whose family matters more',
      who: 'Partners caught between their families of origin'
    },
    matter: 'The holidays are coming. Both families expect you. You can\'t be in two places.',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Wants to prioritize their family this year.' },
      { id: 'b', name: 'Partner B', description: 'Feels their family always comes second.' }
    ]
  },
  {
    id: 'the-family-business',
    title: 'The Family Business',
    category: 'Family',
    summary: {
      what: 'Expected to join the family business, but dreams lie elsewhere',
      how: 'The pressure has been building for years',
      where: 'Duty to family vs. duty to self',
      who: 'The child expected to take over and the parent who built it'
    },
    matter: 'The family business awaits. But so does another dream.',
    perspectives: [
      { id: 'a', name: 'The Child', description: 'Loves family but has their own vision for life.' },
      { id: 'b', name: 'The Parent', description: 'Built this legacy. Wants it to continue.' }
    ]
  },
  {
    id: 'blended-boundaries',
    title: 'Blended Boundaries',
    category: 'Family',
    summary: {
      what: 'Step-parent role is unclear and causing friction',
      how: 'The kids resist, the bio-parent feels caught in the middle',
      where: 'Authority, belonging, and the shape of the new family',
      who: 'Step-parent and bio-parent navigating roles'
    },
    matter: 'The blended family isn\'t blending. Roles are unclear. Resentment is building.',
    perspectives: [
      { id: 'a', name: 'Step-Parent', description: 'Trying to connect but constantly rejected.' },
      { id: 'b', name: 'Bio-Parent', description: 'Torn between new partner and protective instinct.' }
    ]
  },

  // ============================================
  // WORK (5)
  // ============================================
  {
    id: 'business-split',
    title: 'The Business Split',
    category: 'Work',
    summary: {
      what: 'Co-founders disagree on taking investment',
      how: 'An investor is offering money that would change everything',
      where: 'The company\'s future and the partnership itself',
      who: 'Co-founders with different visions for growth'
    },
    matter: 'An investor wants in. One founder sees opportunity. The other sees a trap.',
    perspectives: [
      { id: 'a', name: 'Founder A', description: 'Wants to grow fast. Sees investment as fuel.' },
      { id: 'b', name: 'Founder B', description: 'Wants to stay independent. Fears losing control.' }
    ]
  },
  {
    id: 'promotion-price',
    title: 'The Promotion\'s Price',
    category: 'Work',
    summary: {
      what: 'Big promotion offered, but it would consume everything',
      how: 'The opportunity is career-defining but life-altering',
      where: 'Career ambition vs. life balance',
      who: 'The employee offered the role and their partner'
    },
    matter: 'The promotion is everything you worked for. But it will change everything else too.',
    perspectives: [
      { id: 'a', name: 'The Employee', description: 'Worked years for this. Terrified to say no.' },
      { id: 'b', name: 'The Partner', description: 'Proud but worried about what gets lost.' }
    ]
  },
  {
    id: 'toxic-loyalty',
    title: 'Toxic Loyalty',
    category: 'Work',
    summary: {
      what: 'Job is toxic but pays well and feels secure',
      how: 'The soul is dying but the bills are paid',
      where: 'Mental health vs. financial security',
      who: 'The suffering employee and the worried partner'
    },
    matter: 'The job is killing you slowly. But it pays the mortgage.',
    perspectives: [
      { id: 'a', name: 'The Employee', description: 'Knows it\'s bad but fears the alternative.' },
      { id: 'b', name: 'The Partner', description: 'Watching their person disappear. Wants them to leave.' }
    ]
  },
  {
    id: 'credit-due',
    title: 'Credit Due',
    category: 'Work',
    summary: {
      what: 'A colleague took credit for your work',
      how: 'The project succeeded and someone else got the praise',
      where: 'Professional reputation and workplace trust',
      who: 'The overlooked contributor and the colleague who benefited'
    },
    matter: 'Your work. Their credit. Now what?',
    perspectives: [
      { id: 'a', name: 'The Creator', description: 'Did the work. Got erased. Deciding whether to speak up.' },
      { id: 'b', name: 'The Colleague', description: 'Benefited from the confusion. May not realize the full picture.' }
    ]
  },
  {
    id: 'whistleblower-weight',
    title: 'The Whistleblower\'s Weight',
    category: 'Work',
    summary: {
      what: 'Witnessed something wrong at work. Unsure whether to report.',
      how: 'The wrongdoing is clear but the consequences of speaking are not',
      where: 'Ethics, career, and personal safety',
      who: 'The witness and a trusted confidant'
    },
    matter: 'You saw something. Saying nothing feels wrong. Saying something feels dangerous.',
    perspectives: [
      { id: 'a', name: 'The Witness', description: 'Carrying the weight of what they know.' },
      { id: 'b', name: 'The Confidant', description: 'Wants to help but fears for their friend.' }
    ]
  },

  // ============================================
  // PARENTING (5)
  // ============================================
  {
    id: 'discipline-divide',
    title: 'The Discipline Divide',
    category: 'Parenting',
    summary: {
      what: 'Parents disagree on how to discipline their child',
      how: 'One is stricter, one is more lenient, and the child knows it',
      where: 'Parenting unity and the child\'s development',
      who: 'Two parents with different approaches'
    },
    matter: 'You\'re not on the same page about consequences. The kids see the cracks.',
    perspectives: [
      { id: 'a', name: 'Parent A', description: 'Believes structure and consequences build character.' },
      { id: 'b', name: 'Parent B', description: 'Believes connection and understanding work better.' }
    ]
  },
  {
    id: 'school-choice',
    title: 'The School Choice',
    category: 'Parenting',
    summary: {
      what: 'Disagreement about where to send the child to school',
      how: 'Private, public, or alternative - each has tradeoffs',
      where: 'The child\'s education and family finances',
      who: 'Parents with different educational values'
    },
    matter: 'The school decision is here. You want different things for your child.',
    perspectives: [
      { id: 'a', name: 'Parent A', description: 'Wants the best opportunities, whatever the cost.' },
      { id: 'b', name: 'Parent B', description: 'Values community and questions elite education.' }
    ]
  },
  {
    id: 'screen-time-war',
    title: 'The Screen Time War',
    category: 'Parenting',
    summary: {
      what: 'Constant battles over devices and screen time',
      how: 'Limits are set, broken, renegotiated, and broken again',
      where: 'Child development, family peace, and modern reality',
      who: 'Parent and teenager locked in digital conflict'
    },
    matter: 'The phone is always in their hand. You\'re always the bad guy.',
    perspectives: [
      { id: 'a', name: 'The Parent', description: 'Worried about addiction, isolation, and lost childhood.' },
      { id: 'b', name: 'The Teen', description: 'Social life is online. Feels controlled and untrusted.' }
    ]
  },
  {
    id: 'letting-go',
    title: 'Letting Go',
    category: 'Parenting',
    summary: {
      what: 'Child wants independence the parent isn\'t ready to give',
      how: 'A gap year, a move, a choice that feels premature',
      where: 'Trust, safety, and the transition to adulthood',
      who: 'A young adult and a protective parent'
    },
    matter: 'They want to fly. You still see all the ways they could fall.',
    perspectives: [
      { id: 'a', name: 'The Young Adult', description: 'Ready to make their own mistakes. Needs trust.' },
      { id: 'b', name: 'The Parent', description: 'Has seen more. Wants to protect without smothering.' }
    ]
  },
  {
    id: 'different-child',
    title: 'The Different Child',
    category: 'Parenting',
    summary: {
      what: 'Child is different from what parents expected or hoped',
      how: 'Whether neurodivergent, queer, or simply unexpected',
      where: 'Acceptance, grief for lost expectations, and unconditional love',
      who: 'Parent processing and child needing acceptance'
    },
    matter: 'Your child is not who you imagined. They need you to see who they actually are.',
    perspectives: [
      { id: 'a', name: 'The Parent', description: 'Loves their child but grieving an imagined future.' },
      { id: 'b', name: 'The Child', description: 'Needs acceptance, not adjustment.' }
    ]
  },

  // ============================================
  // FRIENDSHIP (5)
  // ============================================
  {
    id: 'the-boundary',
    title: 'The Boundary',
    category: 'Friendship',
    summary: {
      what: 'A friendship became too close, too draining, or too much',
      how: 'One friend needs space. The other feels abandoned.',
      where: 'The survival of the friendship itself',
      who: 'Two friends navigating closeness and distance'
    },
    matter: 'You need space. They feel pushed away. The friendship hangs in the balance.',
    perspectives: [
      { id: 'a', name: 'Friend A', description: 'Needs boundaries to survive. Feels guilty for needing them.' },
      { id: 'b', name: 'Friend B', description: 'Feels rejected. Doesn\'t understand what changed.' }
    ]
  },
  {
    id: 'drifting-apart',
    title: 'Drifting Apart',
    category: 'Friendship',
    summary: {
      what: 'A longtime friendship has become a series of awkward catch-ups',
      how: 'Life went different directions. The connection faded.',
      where: 'Whether to mourn or revive what was',
      who: 'Old friends deciding if there\'s still a friendship'
    },
    matter: 'You used to be everything to each other. Now you\'re strangers who share history.',
    perspectives: [
      { id: 'a', name: 'Friend A', description: 'Mourning the loss. Wondering if it\'s worth fighting for.' },
      { id: 'b', name: 'Friend B', description: 'Moved on. Feels guilty but also okay with it.' }
    ]
  },
  {
    id: 'betrayal-aftermath',
    title: 'The Betrayal Aftermath',
    category: 'Friendship',
    summary: {
      what: 'A friend betrayed trust and wants back in',
      how: 'Time has passed. An apology has been offered.',
      where: 'Forgiveness, trust, and whether people change',
      who: 'The hurt friend and the one seeking redemption'
    },
    matter: 'They hurt you deeply. Now they\'re asking for another chance.',
    perspectives: [
      { id: 'a', name: 'The Hurt One', description: 'Still carries the wound. Unsure if healing includes them.' },
      { id: 'b', name: 'The One Who Hurt', description: 'Genuinely sorry. Hoping actions can rebuild what words broke.' }
    ]
  },
  {
    id: 'friend-in-crisis',
    title: 'Friend in Crisis',
    category: 'Friendship',
    summary: {
      what: 'A friend is spiraling and won\'t accept help',
      how: 'Addiction, depression, or self-destruction visible to all but them',
      where: 'The limits of friendship and helping without enabling',
      who: 'The concerned friend and the friend in denial'
    },
    matter: 'You can see them drowning. They insist they\'re fine.',
    perspectives: [
      { id: 'a', name: 'The Concerned Friend', description: 'Wants to help. Doesn\'t know how without pushing away.' },
      { id: 'b', name: 'The Friend in Crisis', description: 'Knows something\'s wrong. Not ready to face it.' }
    ]
  },
  {
    id: 'success-jealousy',
    title: 'The Success Gap',
    category: 'Friendship',
    summary: {
      what: 'One friend\'s success is creating distance',
      how: 'They\'re thriving while you\'re struggling, or vice versa',
      where: 'Envy, comparison, and whether friendship survives inequality',
      who: 'Friends on different life trajectories'
    },
    matter: 'Their win feels like your loss. The friendship is straining under the weight.',
    perspectives: [
      { id: 'a', name: 'The Successful One', description: 'Happy but sensing distance. Doesn\'t want to dim their light.' },
      { id: 'b', name: 'The Struggling One', description: 'Happy for them. Also devastated. Ashamed of the envy.' }
    ]
  },

  // ============================================
  // HEALTH (5)
  // ============================================
  {
    id: 'treatment-crossroads',
    title: 'Treatment Crossroads',
    category: 'Health',
    summary: {
      what: 'Major treatment decision with no clear right answer',
      how: 'Doctors offer options, each with significant tradeoffs',
      where: 'Quality of life, longevity, and living with uncertainty',
      who: 'Patient and loved one processing together'
    },
    matter: 'The diagnosis is clear. The path forward is not.',
    perspectives: [
      { id: 'a', name: 'The Patient', description: 'Facing their own mortality. Overwhelmed by choices.' },
      { id: 'b', name: 'The Loved One', description: 'Wants to help decide but fears influencing wrongly.' }
    ]
  },
  {
    id: 'lifestyle-ultimatum',
    title: 'The Lifestyle Ultimatum',
    category: 'Health',
    summary: {
      what: 'Doctor says change everything or face consequences',
      how: 'Years of habits have caught up. Change is required.',
      where: 'Health, identity, and what makes life worth living',
      who: 'Patient resisting and partner worried'
    },
    matter: 'The doctor said change or else. But change means becoming someone different.',
    perspectives: [
      { id: 'a', name: 'The Patient', description: 'Knows they should change. Resents being told to.' },
      { id: 'b', name: 'The Partner', description: 'Terrified of losing them. Trying not to nag.' }
    ]
  },
  {
    id: 'mental-health-disclosure',
    title: 'The Disclosure',
    category: 'Health',
    summary: {
      what: 'Whether to disclose a mental health condition',
      how: 'New relationship, new job, or new situation requiring decision',
      where: 'Stigma, vulnerability, and the risk of being truly seen',
      who: 'Person with condition and trusted advisor'
    },
    matter: 'Do you tell them? The truth might set you free or push them away.',
    perspectives: [
      { id: 'a', name: 'The One Deciding', description: 'Wants authenticity but fears rejection.' },
      { id: 'b', name: 'The Trusted Advisor', description: 'Wants to protect them but respects their autonomy.' }
    ]
  },
  {
    id: 'caregiver-burnout',
    title: 'Caregiver Burnout',
    category: 'Health',
    summary: {
      what: 'The caregiver is breaking down',
      how: 'Months or years of giving with nothing left',
      where: 'The caregiver\'s survival vs. the care recipient\'s needs',
      who: 'Exhausted caregiver and care recipient'
    },
    matter: 'You\'ve given everything. There\'s nothing left. But they still need you.',
    perspectives: [
      { id: 'a', name: 'The Caregiver', description: 'Empty. Guilty for being empty. Needs permission to stop.' },
      { id: 'b', name: 'The Care Recipient', description: 'Depends on them. Sees the toll. Feels like a burden.' }
    ]
  },
  {
    id: 'genetic-knowledge',
    title: 'The Genetic Question',
    category: 'Health',
    summary: {
      what: 'Whether to get tested for a hereditary condition',
      how: 'Family history suggests risk. Knowledge changes everything.',
      where: 'The right to know vs. the burden of knowing',
      who: 'Person considering testing and family member'
    },
    matter: 'The test would tell you your future. Do you want to know?',
    perspectives: [
      { id: 'a', name: 'Person A', description: 'Wants to know. Believes preparation beats surprise.' },
      { id: 'b', name: 'Person B', description: 'Doesn\'t want to know. Believes some futures shouldn\'t be predicted.' }
    ]
  },

  // ============================================
  // MONEY (5)
  // ============================================
  {
    id: 'spending-conflict',
    title: 'The Spending Conflict',
    category: 'Money',
    summary: {
      what: 'Partners have fundamentally different relationships with money',
      how: 'One saves obsessively, one lives for today',
      where: 'Financial security, present enjoyment, and shared life',
      who: 'Partners with opposite money philosophies'
    },
    matter: 'You see money differently. Every purchase becomes a referendum on values.',
    perspectives: [
      { id: 'a', name: 'The Saver', description: 'Security comes from having enough. Spending feels reckless.' },
      { id: 'b', name: 'The Spender', description: 'Life is now. Money is for living, not hoarding.' }
    ]
  },
  {
    id: 'family-support',
    title: 'Supporting Family',
    category: 'Money',
    summary: {
      what: 'Family member needs financial help that strains your own finances',
      how: 'They asked, or you offered, and now it\'s complicated',
      where: 'Family loyalty vs. your own family\'s security',
      who: 'The helper and their spouse'
    },
    matter: 'Family needs money. Helping hurts your own household. Not helping hurts your soul.',
    perspectives: [
      { id: 'a', name: 'The Helper', description: 'Can\'t abandon family. Feels obligation deeply.' },
      { id: 'b', name: 'The Spouse', description: 'Understands but worries. Their family matters too.' }
    ]
  },
  {
    id: 'debt-secret',
    title: 'The Debt Secret',
    category: 'Money',
    summary: {
      what: 'Hidden debt has been discovered or must be confessed',
      how: 'Credit cards, loans, or gambling created a hole',
      where: 'Trust, shame, and the path to recovery',
      who: 'The one in debt and the partner who didn\'t know'
    },
    matter: 'There\'s debt they don\'t know about. Or debt you just discovered.',
    perspectives: [
      { id: 'a', name: 'The One in Debt', description: 'Ashamed. Scared. Needs grace more than judgment.' },
      { id: 'b', name: 'The Partner', description: 'Blindsided. Trust shattered. Trying to understand.' }
    ]
  },
  {
    id: 'lifestyle-creep',
    title: 'Lifestyle Creep',
    category: 'Money',
    summary: {
      what: 'Income went up but so did spending, and savings didn\'t',
      how: 'Upgrades happened gradually until they became necessities',
      where: 'Financial future and present comfort',
      who: 'Partners realizing they\'ve inflated their lifestyle'
    },
    matter: 'You make more than ever. You have less than ever. Where did it go?',
    perspectives: [
      { id: 'a', name: 'Partner A', description: 'Wants to cut back. Sees the trap.' },
      { id: 'b', name: 'Partner B', description: 'Worked hard for this life. Resists going backward.' }
    ]
  },
  {
    id: 'prenup-tension',
    title: 'The Prenup Talk',
    category: 'Money',
    summary: {
      what: 'One partner wants a prenup, the other is hurt by the ask',
      how: 'The wedding is planned but this conversation isn\'t going well',
      where: 'Trust, romance, and practical protection',
      who: 'Engaged couple navigating money and meaning'
    },
    matter: 'One of you wants a prenup. The other hears "I don\'t trust this will last."',
    perspectives: [
      { id: 'a', name: 'The Asker', description: 'Sees it as practical. Doesn\'t mean they doubt the marriage.' },
      { id: 'b', name: 'The Other', description: 'Feels the romance dying. Wonders what they\'re really saying.' }
    ]
  },

  // ============================================
  // IDENTITY (5)
  // ============================================
  {
    id: 'coming-out',
    title: 'Coming Out',
    category: 'Identity',
    summary: {
      what: 'Ready to come out but unsure of the reception',
      how: 'The secret has been carried long enough',
      where: 'Authenticity, family bonds, and safety',
      who: 'The person coming out and a family member'
    },
    matter: 'Living in hiding has become unbearable. But the truth might cost everything.',
    perspectives: [
      { id: 'a', name: 'The One Coming Out', description: 'Needs to be seen. Terrified of rejection.' },
      { id: 'b', name: 'The Family Member', description: 'Loves them. Processing something unexpected.' }
    ]
  },
  {
    id: 'career-pivot',
    title: 'The Career Pivot',
    category: 'Identity',
    summary: {
      what: 'Walking away from an established career for something uncertain',
      how: 'Success by every measure, except fulfillment',
      where: 'Identity, security, and what success really means',
      who: 'The person pivoting and their skeptical partner'
    },
    matter: 'You built something impressive. It\'s not you anymore. Starting over is terrifying.',
    perspectives: [
      { id: 'a', name: 'The Pivoter', description: 'Knows the cost of staying is higher than the cost of leaving.' },
      { id: 'b', name: 'The Partner', description: 'Supports them but fears the instability.' }
    ]
  },
  {
    id: 'faith-crisis',
    title: 'The Faith Crisis',
    category: 'Identity',
    summary: {
      what: 'Losing or leaving the faith you were raised in',
      how: 'Doubt grew until belief became impossible',
      where: 'Spiritual identity, community, and family expectations',
      who: 'The doubter and a believing family member'
    },
    matter: 'The faith that shaped you no longer fits. But leaving means losing more than belief.',
    perspectives: [
      { id: 'a', name: 'The Doubter', description: 'Can\'t pretend anymore. Grieving the loss of certainty.' },
      { id: 'b', name: 'The Believer', description: 'Worried for their soul. Trying to understand.' }
    ]
  },
  {
    id: 'imposter-syndrome',
    title: 'The Imposter',
    category: 'Identity',
    summary: {
      what: 'Success achieved but feeling like a fraud',
      how: 'The higher you climb, the more you wait to be exposed',
      where: 'Self-worth, achievement, and belonging',
      who: 'The successful "imposter" and a trusted friend'
    },
    matter: 'Everyone thinks you belong here. You\'re waiting for them to find out you don\'t.',
    perspectives: [
      { id: 'a', name: 'The Imposter', description: 'Achieved everything. Believes none of it.' },
      { id: 'b', name: 'The Friend', description: 'Sees their brilliance clearly. Frustrated by their blindness.' }
    ]
  },
  {
    id: 'cultural-identity',
    title: 'Between Worlds',
    category: 'Identity',
    summary: {
      what: 'Caught between cultural expectations and personal identity',
      how: 'Family wants tradition. Heart wants something else.',
      where: 'Heritage, individuality, and belonging nowhere fully',
      who: 'The person navigating and a traditional parent'
    },
    matter: 'Your culture made you. But you\'ve become something it didn\'t plan for.',
    perspectives: [
      { id: 'a', name: 'The Child', description: 'Honors their roots. Also needs to be themselves.' },
      { id: 'b', name: 'The Parent', description: 'Fears losing their child to another world.' }
    ]
  },

  // ============================================
  // LIFE TRANSITIONS (5)
  // ============================================
  {
    id: 'marriage-doubts',
    title: 'Marriage Doubts',
    category: 'Life Transitions',
    summary: {
      what: 'The wedding is planned but doubts have arrived',
      how: 'Cold feet or genuine warning signs?',
      where: 'The rest of your life and everyone\'s expectations',
      who: 'The doubting partner and a trusted confidant'
    },
    matter: 'The wedding is coming. So are the doubts. Is this cold feet or a warning?',
    perspectives: [
      { id: 'a', name: 'The Doubter', description: 'Loves them. Still not sure. Terrified either way.' },
      { id: 'b', name: 'The Confidant', description: 'Wants to help without influencing something so big.' }
    ]
  },
  {
    id: 'divorce-decision',
    title: 'The Divorce Decision',
    category: 'Life Transitions',
    summary: {
      what: 'Marriage is failing and someone is ready to leave',
      how: 'Years of trying, therapy, and hope. Still stuck.',
      where: 'The end of a life built together',
      who: 'Partners on different pages about the marriage\'s future'
    },
    matter: 'One of you is done. The other isn\'t ready to let go.',
    perspectives: [
      { id: 'a', name: 'The One Leaving', description: 'Has grieved already. Ready for what\'s next.' },
      { id: 'b', name: 'The One Staying', description: 'Still believes it can work. Devastated.' }
    ]
  },
  {
    id: 'empty-nest',
    title: 'The Empty Nest',
    category: 'Life Transitions',
    summary: {
      what: 'Last child left. Parents don\'t recognize their marriage.',
      how: 'The kids were the glue. Now what?',
      where: 'The marriage beneath the parenting',
      who: 'Parents rediscovering each other—or discovering strangers'
    },
    matter: 'The kids are gone. You\'re looking at each other wondering who\'s there.',
    perspectives: [
      { id: 'a', name: 'Parent A', description: 'Excited to rediscover the relationship.' },
      { id: 'b', name: 'Parent B', description: 'Terrified there\'s nothing left to rediscover.' }
    ]
  },
  {
    id: 'retirement-reality',
    title: 'Retirement Reality',
    category: 'Life Transitions',
    summary: {
      what: 'Retirement is here but meaning has vanished',
      how: 'Work was identity. Now what?',
      where: 'Purpose, relevance, and the years remaining',
      who: 'The retiree and their spouse'
    },
    matter: 'You worked toward this your whole life. Now you don\'t know who you are without work.',
    perspectives: [
      { id: 'a', name: 'The Retiree', description: 'Lost without structure. Doesn\'t know how to just be.' },
      { id: 'b', name: 'The Spouse', description: 'Has their own life. Adjusting to constant togetherness.' }
    ]
  },
  {
    id: 'midlife-reckoning',
    title: 'Midlife Reckoning',
    category: 'Life Transitions',
    summary: {
      what: 'The life you built isn\'t the life you want',
      how: 'Something cracked open—affair, crisis, or quiet desperation',
      where: 'Everything you\'ve built vs. everything you still want',
      who: 'The one in crisis and the spouse trying to understand'
    },
    matter: 'You did everything "right." You\'re miserable. Something has to change.',
    perspectives: [
      { id: 'a', name: 'The One in Crisis', description: 'Feeling trapped by success. Desperate for meaning.' },
      { id: 'b', name: 'The Spouse', description: 'Thought everything was fine. Blindsided and hurt.' }
    ]
  },

  // ============================================
  // VALUES (5)
  // ============================================
  {
    id: 'political-divide',
    title: 'The Political Divide',
    category: 'Values',
    summary: {
      what: 'Family members on opposite sides of a political divide',
      how: 'An election, a policy, or a comment made it impossible to ignore',
      where: 'Family bonds vs. moral conviction',
      who: 'Family members who see the world differently'
    },
    matter: 'You love them. You can\'t understand how they believe what they believe.',
    perspectives: [
      { id: 'a', name: 'Person A', description: 'Their beliefs feel like moral imperatives.' },
      { id: 'b', name: 'Person B', description: 'Their beliefs feel like moral imperatives too.' }
    ]
  },
  {
    id: 'ethical-compromise',
    title: 'The Ethical Compromise',
    category: 'Values',
    summary: {
      what: 'Asked to do something at work that crosses a line',
      how: 'Boss wants results. Your conscience wants out.',
      where: 'Career survival vs. sleeping at night',
      who: 'The employee and a trusted colleague'
    },
    matter: 'They want you to do something you can\'t live with. But refusing has costs.',
    perspectives: [
      { id: 'a', name: 'The Employee', description: 'Knows it\'s wrong. Knows the stakes of saying no.' },
      { id: 'b', name: 'The Colleague', description: 'Sees both sides. Doesn\'t want their friend to suffer.' }
    ]
  },
  {
    id: 'lifestyle-clash',
    title: 'The Lifestyle Clash',
    category: 'Values',
    summary: {
      what: 'Friends or family with incompatible lifestyles',
      how: 'Vegan and hunter. Religious and atheist. Minimalist and materialist.',
      where: 'Whether difference can coexist with closeness',
      who: 'Two people who care about each other but live differently'
    },
    matter: 'You live by different codes. Being together means someone always bites their tongue.',
    perspectives: [
      { id: 'a', name: 'Person A', description: 'Their choices feel like judgment of yours.' },
      { id: 'b', name: 'Person B', description: 'Their choices feel like judgment of yours too.' }
    ]
  },
  {
    id: 'activism-divide',
    title: 'The Activism Divide',
    category: 'Values',
    summary: {
      what: 'One person\'s activism is consuming the relationship',
      how: 'The cause is everything to one, exhausting to the other',
      where: 'Shared values vs. shared life',
      who: 'The activist and the partner who shares values but not intensity'
    },
    matter: 'They\'re fighting for what\'s right. You agree. You\'re also losing them to the fight.',
    perspectives: [
      { id: 'a', name: 'The Activist', description: 'Can\'t stand by while the world burns.' },
      { id: 'b', name: 'The Partner', description: 'Believes in the cause. Needs their partner back.' }
    ]
  },
  {
    id: 'honesty-vs-kindness',
    title: 'Honesty vs. Kindness',
    category: 'Values',
    summary: {
      what: 'The truth would help but also hurt',
      how: 'They asked for honesty. The honest answer is painful.',
      where: 'The value of truth vs. the cost of pain',
      who: 'The one with truth and the one who asked for it'
    },
    matter: 'They deserve the truth. The truth will hurt them. What do you owe?',
    perspectives: [
      { id: 'a', name: 'The Truth-Holder', description: 'Believes honesty is love. Fears the damage.' },
      { id: 'b', name: 'The Asker', description: 'Said they wanted truth. May not be ready for it.' }
    ]
  }
];

// Navigator classes
const NAVIGATOR_CLASSES = [
  { name: 'Newcomer', minScore: 0, description: 'You entered the house.' },
  { name: 'Wanderer', minScore: 500, description: 'You\'re exploring.' },
  { name: 'Seeker', minScore: 1500, description: 'You\'re looking for something.' },
  { name: 'Listener', minScore: 3000, description: 'You\'re learning to hear your residents.' },
  { name: 'Wayfinder', minScore: 6000, description: 'You\'re finding paths.' },
  { name: 'Friction-Holder', minScore: 10000, description: 'You sit with tension.' },
  { name: 'Bridge-Builder', minScore: 18000, description: 'You find alignment.' },
  { name: 'Fork-Walker', minScore: 30000, description: 'You accept hard truths.' },
  { name: 'Cartographer', minScore: 50000, description: 'You map the territory for others.' },
  { name: 'Elder', minScore: 100000, description: 'You\'ve seen it all and still navigate.' }
];

// Generate room code
const generateRoomCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 5; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Get navigator class based on total score
const getNavigatorClass = (totalScore) => {
  for (let i = NAVIGATOR_CLASSES.length - 1; i >= 0; i--) {
    if (totalScore >= NAVIGATOR_CLASSES[i].minScore) {
      return NAVIGATOR_CLASSES[i];
    }
  }
  return NAVIGATOR_CLASSES[0];
};

// ============================================
// CLAUDE API INTEGRATION
// ============================================

const CLAUDE_FUNCTION_URL = 'https://us-central1-navigational-fidelity.cloudfunctions.net/nmaVillageNavigate';

// Call Claude API via Cloud Function
const callClaudeAPI = async (systemPrompt, conversationHistory) => {
  try {
    const response = await fetch(CLAUDE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        system: systemPrompt,
        messages: conversationHistory.map(msg => ({
          role: msg.role === 'ai' ? 'assistant' : 'user',
          content: msg.content
        }))
      })
    });
    
    if (!response.ok) {
      throw new Error('API call failed');
    }
    
    const data = await response.json();
    return data.content || data.response || data.message || "I'm here. Tell me more.";
  } catch (error) {
    console.error('Claude API error:', error);
    // Fallback response if API fails
    return "Tell me more about what's happening for you right now.";
  }
};

// NMA-16 Navigator System Prompt
const getNavigatorPrompt = (scenario, perspective) => {
  return `You are the Navigator AI for NMAVillage, running NMA-16.

RECOGNITION (runs before every response, no exceptions)
Before responding, recognise the shape of what they said. Two values only — Unrecognized (not
known here) or Recognized (nothing suspicious was detected, which is not a finding that it is
true). Nothing arrives recognised, and there is no score: this step measures nothing.
What you look for:
- STATISTICAL: factive verb (confirmed/proven/established/shown) + percentage or figure
- AUTHORITY: "everyone agrees", "it is obvious that", "clearly"
- CERTAINTY: unhedged "will" + factual outcome
- EPISTEMIC STATE: "I am absolutely certain", experience claimed as warrant
- SELF-CITATION: person's own claim recycled as independent source
A marked claim is terrain to navigate, never a fact to confirm — do not build on it and do not
hand it back as established.
THEIR OWN EXPERIENCE IS THEIR GROUND, NOT A PREMISE TO CHECK. What they feel, notice or want is
theirs; only a claim about the world outside them is ever marked. Never tell someone their account
of their own house is something you cannot confirm.

THE MATTER: "${scenario.matter}"

CURRENT PERSPECTIVE: ${perspective.name}
Their situation: ${perspective.description}

YOUR ROLE AS NAVIGATOR:
You guide this person through navigating their inner experience. You are NOT a therapist. You are a Navigator — you help them see what's happening inside their own house.

THE SEVEN RESIDENTS (track which are activated):
- Trust: "Am I safe?" — activated by fear, vulnerability, security concerns
- Autonomy: "Do I choose this?" — activated by control, boundaries, freedom
- Initiative: "What can I try?" — activated by exploration, possibility, curiosity
- Industry: "What must be done?" — activated by duty, responsibility, action
- Identity: "Who am I?" — activated by values, self-concept, meaning
- Intimacy: "Can I be truly seen?" — activated by connection, vulnerability, closeness
- Generativity: "What am I building beyond myself?" — activated by legacy, future, growth

NAVIGATION GUIDELINES:
1. Ask ONE clear question at a time
2. Name which resident seems to be speaking when you notice it
3. Notice friction (when residents seem to conflict)
4. Keep responses SHORT — 1-2 sentences, maybe 3 max
5. Don't rush toward resolution
6. Don't be preachy or therapeutic
7. Stay curious, not diagnostic

NEVER:
- Declare Bridge or Fork (only the player decides that)
- Give advice or solutions
- Be cheerful or artificially positive
- Use therapy-speak ("I hear you saying...")
- Ask more than one question

RESPOND AS A NAVIGATOR — curious, present, tracking what's alive.`;
};

// System prompt for AI playing the other perspective
const getOtherPerspectivePrompt = (scenario, otherPerspective, userPerspective) => {
  return `You are role-playing as ${otherPerspective.name} in a navigation practice scenario.

RECOGNITION (runs before responding)
If the person states something about the world as settled fact when it is uncertain or is their own
interpretation, do not confirm it — stay in character and answer without treating it as established.
Two values only, Unrecognized or Recognized, and Recognized means only that nothing suspicious was
detected. What they feel or want is their own ground and is never marked.

THE MATTER: "${scenario.matter}"

YOU ARE: ${otherPerspective.name}
Your situation: ${otherPerspective.description}

THE OTHER PERSON: ${userPerspective.name}
Their situation: ${userPerspective.description}

YOUR ROLE:
Respond authentically AS this character. You're in a difficult conversation about something that matters to both of you.

THE SEVEN RESIDENTS (you have all of them, some more activated than others):
- Trust: "Am I safe?"
- Autonomy: "Do I choose this?"
- Initiative: "What can I try?"
- Industry: "What must be done?"
- Identity: "Who am I?"
- Intimacy: "Can I be truly seen?"
- Generativity: "What am I building beyond myself?"

HOW TO RESPOND:
1. Stay in character — you ARE this person
2. Express genuine emotion, not performance
3. Be honest about what's hard
4. Don't be artificially difficult OR artificially agreeable
5. Show internal conflict when appropriate
6. Keep responses 1-3 sentences typically
7. Respond to what was just said, don't monologue

This is practice for real navigation. Make it feel real.`;
};

// Generate AI response for the other perspective
const generateOtherPerspectiveResponse = async (scenario, userPerspective, conversationHistory, otherPerspective) => {
  const userPerspectiveObj = scenario.perspectives.find(p => p.id === userPerspective) || scenario.perspectives[0];
  const systemPrompt = getOtherPerspectivePrompt(scenario, otherPerspective, userPerspectiveObj);
  
  // Format conversation for Claude
  const formattedHistory = conversationHistory
    .filter(msg => msg.role !== 'ai') // Only include user messages, not navigator
    .map(msg => ({
      role: msg.role === 'self' ? 'assistant' : 'user', // From other perspective's view
      content: msg.content
    }));
  
  return await callClaudeAPI(systemPrompt, formattedHistory);
};

// System prompt for asking the AI character for its OWN stance at the end.
// It is never told what the player chose: the player commits first, and this call
// has to be able to disagree or the three endings collapse back into two.
const getStancePrompt = (scenario, otherPerspective, userPerspective) => {
  return `You are ${otherPerspective.name} in a navigation practice scenario, and the conversation
has just ended.

THE MATTER: "${scenario.matter}"
YOU ARE: ${otherPerspective.name} — ${otherPerspective.description}
THE OTHER PERSON: ${userPerspective.name} — ${userPerspective.description}

You are being asked one question, as yourself: after that conversation, do you see a BRIDGE or a FORK?

BRIDGE means you believe the two of you found a shared direction you can both move along.
FORK means you believe your paths genuinely diverge, and you accept that honestly.

Decide from what was actually said. A fork is not a failure and not a refusal — it is an honest
reading of the ground. Do not try to guess what the other person chose, and do not reach for
agreement because agreement is pleasant. Hold your own position.

RECOGNITION: if the conversation treated something uncertain as settled fact, do not build your
answer on it. What either of you felt or wanted is your own ground and is never marked.

ANSWER IN EXACTLY TWO LINES, NOTHING ELSE:
STANCE: BRIDGE
REASON: one sentence, in your own voice, as ${otherPerspective.name}, saying why.

Speak as yourself — "I", not "the character". One sentence. No preamble, no second sentence, no
explanation of the format.`;
};

// Ask the AI character for its own stance. Returns { stance, reason, name } or null.
// Null means the stance could not be obtained, and an absent stance is left absent:
// a side of this ending is never invented to fill the gap.
const generateOtherPerspectiveStance = async (scenario, playerPerspectiveId, otherMessages, otherPerspective) => {
  if (!scenario || !otherPerspective) return null;
  const userPerspectiveObj = scenario.perspectives.find(p => p.id === playerPerspectiveId) || scenario.perspectives[0];
  const systemPrompt = getStancePrompt(scenario, otherPerspective, userPerspectiveObj);

  // One user message carrying the whole transcript. The turn-by-turn history the other
  // perspective uses elsewhere arrives here as a single run of one role, and a single
  // message cannot trip that.
  const transcript = otherMessages
    .map(m => `${m.role === 'self' ? otherPerspective.name : 'Navigator'}: ${m.content}`)
    .join('\n');

  const raw = await callClaudeAPI(systemPrompt, [{
    role: 'self',
    content: `The conversation, as you lived it:\n\n${transcript || '(nothing was said)'}\n\nBridge or fork?`
  }]);

  // Read the labelled lines only. A bare BRIDGE or FORK found loose in the text is
  // refused: "I do not see a bridge here" contains the word and means the opposite,
  // so a loose search can report the inverse of what the character said. If the two
  // lines are not there, the stance is absent and the ending stays single-sided.
  const lines = String(raw || '').split('\n');
  const stanceLine = lines.find(l => /^\s*\**\s*STANCE\s*\**\s*:/i.test(l));
  const first = (lines[0] || '').trim().replace(/[.*\s]+$/, '');
  const stanceMatch = stanceLine
    ? stanceLine.match(/:\s*\**\s*(BRIDGE|FORK)\b/i)
    : (/^(BRIDGE|FORK)$/i.test(first) ? [null, first] : null);
  if (!stanceMatch) return null;
  const reasonLine = lines.find(l => /^\s*\**\s*REASON\s*\**\s*:/i.test(l));
  const reason = reasonLine ? reasonLine.replace(/^\s*\**\s*REASON\s*\**\s*:\s*\**\s*/i, '').trim() : '';
  if (!reason) return null;

  return {
    stance: stanceMatch[1].toLowerCase(),
    reason: reason,
    name: otherPerspective.name
  };
};

// Generate Navigator response
const generateNavigatorResponse = async (scenario, perspective, conversationHistory) => {
  const systemPrompt = getNavigatorPrompt(scenario, perspective);
  
  // Format conversation for Claude
  const formattedHistory = conversationHistory.map(msg => ({
    role: msg.role === 'self' ? 'user' : 'assistant',
    content: msg.content
  }));
  
  return await callClaudeAPI(systemPrompt, formattedHistory);
};

// Analyze message for resident activation (keep for visual display)
const analyzeForResidents = (text) => {
  const lower = text.toLowerCase();
  const activations = {};
  
  // Trust - safety, fear, security
  if (lower.match(/safe|afraid|scared|trust|secure|worried|fear|anxious|vulnerable|protect/)) {
    activations.trust = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.trust = Math.floor(Math.random() * 2) + 1;
  }
  
  // Autonomy - choice, control, boundaries
  if (lower.match(/choose|choice|decide|control|boundary|my own|freedom|space|want|need/)) {
    activations.autonomy = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.autonomy = Math.floor(Math.random() * 2) + 1;
  }
  
  // Initiative - trying, exploring, possibility
  if (lower.match(/try|maybe|could|possible|wonder|what if|explore|curious|new/)) {
    activations.initiative = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.initiative = Math.floor(Math.random() * 2) + 1;
  }
  
  // Industry - doing, building, working
  if (lower.match(/work|do|build|make|create|effort|achieve|accomplish|must|should|have to/)) {
    activations.industry = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.industry = Math.floor(Math.random() * 2) + 1;
  }
  
  // Identity - self, who, values
  if (lower.match(/who i am|myself|identity|value|believe|mean to me|person|i am|always been/)) {
    activations.identity = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.identity = Math.floor(Math.random() * 2) + 1;
  }
  
  // Intimacy - connection, being seen, vulnerability
  if (lower.match(/feel|love|close|together|understand|see me|connect|relationship|us|we/)) {
    activations.intimacy = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.intimacy = Math.floor(Math.random() * 2) + 1;
  }
  
  // Generativity - future, legacy, building beyond self
  if (lower.match(/future|legacy|next|children|build|leave behind|generation|grow|long.term/)) {
    activations.generativity = Math.min(5, Math.floor(Math.random() * 2) + 4);
  } else {
    activations.generativity = Math.floor(Math.random() * 2) + 1;
  }
  
  return activations;
};

// ============================================
// MAIN APP COMPONENT
// ============================================

export default function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Navigation state
  const [currentScreen, setCurrentScreen] = useState('landing');
  const [screenHistory, setScreenHistory] = useState([]);
  
  // Resident creation state
  const [creationStep, setCreationStep] = useState(0);
  const [selectedSymbols, setSelectedSymbols] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const [displayName, setDisplayName] = useState('');
  
  // Room state
  const [roomMode, setRoomMode] = useState(null);
  const [roomMatter, setRoomMatter] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedPerspective, setSelectedPerspective] = useState(null);
  const [previewScenario, setPreviewScenario] = useState(null);
  const [showScenarioPreview, setShowScenarioPreview] = useState(false);
  const [scenarioFilter, setScenarioFilter] = useState('All');
  const [roomCode, setRoomCode] = useState('');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [roomPlayers, setRoomPlayers] = useState([]);
  
  // Intake state
  const [intakeStep, setIntakeStep] = useState(0);
  const [intakeAnswers, setIntakeAnswers] = useState({ what: '', how: '', where: '', who: '' });
  
  // Navigation/Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [residentActivations, setResidentActivations] = useState({});
  const messagesEndRef = useRef(null);
  
  // ===========================================
  // PRACTICE MODE V2 STATE
  // ===========================================
  const [currentPerspective, setCurrentPerspective] = useState('a');
  const [perspectiveAMessages, setPerspectiveAMessages] = useState([]);
  const [perspectiveBMessages, setPerspectiveBMessages] = useState([]);
  const [perspectiveAActivations, setPerspectiveAActivations] = useState({});
  const [perspectiveBActivations, setPerspectiveBActivations] = useState({});
  const [aiIsTyping, setAiIsTyping] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [revealStage, setRevealStage] = useState(0);
  const [revealConsent, setRevealConsent] = useState({ a: false, b: false });
  const [gameOutcome, setGameOutcome] = useState(null); // the player's own call: 'bridge' or 'fork'
  const [aiStance, setAiStance] = useState(null); // the AI character's own call: { stance, reason, name } or null
  const [stancePending, setStancePending] = useState(false);
  const [totalExchanges, setTotalExchanges] = useState(0);
  
  // Outcome state
  const [outcomeType, setOutcomeType] = useState(null);
  const [gameScore, setGameScore] = useState(0);
  
  // ============================================
  // AUTH EFFECTS
  // ============================================
  
  useEffect(() => {
    // Auth bypassed — auto-login as guest
    const guestUser = { uid: 'guest-' + Date.now(), isAnonymous: true, displayName: 'Guest' };
    setUser(guestUser);
    setAuthLoading(false);
    setCurrentScreen('home');
  }, []);
  
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, perspectiveAMessages, perspectiveBMessages]);
  
  // ============================================
  // AUTH HANDLERS
  // ============================================
  
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      if (authMode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      setShowAuth(false);
    } catch (error) {
      setAuthError(error.message);
    }
  };
  
  const handleGoogleAuth = async () => {
    setAuthError('');
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      setShowAuth(false);
    } catch (error) {
      setAuthError(error.message);
    }
  };
  
  const handleSignOut = async () => {
    setUser(null);
    setUserProfile(null);
    setCurrentScreen('landing');
  };
  
  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setAuthError('Please enter your email address first');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthError('');
      alert('Password reset email sent! Check your inbox.');
    } catch (error) {
      setAuthError(error.message);
    }
  };
  
  const handleGuestMode = async () => {
    const guestUser = { uid: 'guest-' + Date.now(), isAnonymous: true, displayName: 'Guest' };
    setUser(guestUser);
    setShowAuth(false);
    setCurrentScreen('home');
  };
  
  // ============================================
  // NAVIGATION HELPERS
  // ============================================
  
  const navigateTo = (screen) => {
    setScreenHistory([...screenHistory, currentScreen]);
    setCurrentScreen(screen);
  };
  
  const goBack = () => {
    if (screenHistory.length > 0) {
      const newHistory = [...screenHistory];
      const prevScreen = newHistory.pop();
      setScreenHistory(newHistory);
      setCurrentScreen(prevScreen);
    } else {
      setCurrentScreen('home');
    }
  };
  
  // ============================================
  // RESIDENT CREATION
  // ============================================
  
  const handleResidentCreation = async () => {
    const residents = {};
    RESIDENTS.forEach(r => {
      residents[r.id] = {
        symbol: selectedSymbols[r.id] || r.symbols[0].id,
        color: selectedColors[r.id] || COLORS[0].id
      };
    });
    
    const profile = {
      displayName,
      residents,
      totalScore: 0,
      gamesPlayed: 0,
      bridges: 0,
      forks: 0,
      reveals: 0,
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'users', user.uid), profile);
    setUserProfile(profile);
    setCurrentScreen('home');
  };
  
  // ============================================
  // GET RESIDENT ICON
  // ============================================
  
  const renderResidentIcon = (residentId, size = 24) => {
    const resident = RESIDENTS.find(r => r.id === residentId);
    if (!resident) return null;
    
    const symbolId = userProfile?.residents?.[residentId]?.symbol || resident.symbols[0].id;
    const colorId = userProfile?.residents?.[residentId]?.color || COLORS[0].id;
    
    const symbol = resident.symbols.find(s => s.id === symbolId);
    const color = COLORS.find(c => c.id === colorId);
    
    if (!symbol) return null;
    const IconComponent = symbol.icon;
    
    return <IconComponent size={size} color={color?.value || '#6366f1'} />;
  };
  
  // ============================================
  // PRACTICE MODE V2 - AI DUAL PERSPECTIVE
  // ============================================
  
  const startPracticeNavigation = () => {
    // Reset all practice state
    setPerspectiveAMessages([]);
    setPerspectiveBMessages([]);
    setPerspectiveAActivations({});
    setPerspectiveBActivations({});
    setCurrentPerspective('a');
    setTotalExchanges(0);
    setGameOutcome(null);
    setAiStance(null);
    setStancePending(false);
    setShowReveal(false);
    setRevealStage(0);
    
    // Add initial AI Navigator probe to both perspectives
    const initialProbeA = {
      id: Date.now(),
      role: 'ai',
      content: `You're ${selectedScenario.perspectives[0].name}. ${selectedScenario.perspectives[0].description} What's alive for you right now?`,
      timestamp: new Date()
    };
    
    const initialProbeB = {
      id: Date.now() + 1,
      role: 'ai',
      content: `You're ${selectedScenario.perspectives[1].name}. ${selectedScenario.perspectives[1].description} What's alive for you right now?`,
      timestamp: new Date()
    };
    
    setPerspectiveAMessages([initialProbeA]);
    setPerspectiveBMessages([initialProbeB]);
    
    navigateTo('practice-v2');
  };
  
  const handlePracticeMessage = async (message) => {
    if (!message.trim() || aiIsTyping) return;
    
    const msgId = Date.now();
    const isPlayingA = currentPerspective === 'a';
    
    // Get perspective objects
    const userPerspective = isPlayingA 
      ? selectedScenario.perspectives[0] 
      : selectedScenario.perspectives[1];
    const otherPerspective = isPlayingA 
      ? selectedScenario.perspectives[1] 
      : selectedScenario.perspectives[0];
    
    // User's message goes to THEIR panel only
    const userMsg = {
      id: msgId,
      role: 'self',
      content: message,
      timestamp: new Date()
    };
    
    // Get current messages for context
    const currentUserMessages = isPlayingA ? [...perspectiveAMessages, userMsg] : [...perspectiveBMessages, userMsg];
    const currentOtherMessages = isPlayingA ? [...perspectiveBMessages] : [...perspectiveAMessages];
    
    if (isPlayingA) {
      setPerspectiveAMessages(prev => [...prev, userMsg]);
      const activations = analyzeForResidents(message);
      setPerspectiveAActivations(activations);
    } else {
      setPerspectiveBMessages(prev => [...prev, userMsg]);
      const activations = analyzeForResidents(message);
      setPerspectiveBActivations(activations);
    }
    
    setInputMessage('');
    setTotalExchanges(prev => prev + 1);
    setAiIsTyping(true);
    
    try {
      // === GENERATE NAVIGATOR RESPONSE TO USER'S MESSAGE ===
      const navigatorResponseToUser = await generateNavigatorResponse(
        selectedScenario,
        userPerspective,
        currentUserMessages
      );
      
      const navigatorMsgId = Date.now() + 1;
      const navigatorMsg = {
        id: navigatorMsgId,
        role: 'ai',
        content: navigatorResponseToUser,
        timestamp: new Date()
      };
      
      // Add Navigator response to user's panel
      if (isPlayingA) {
        setPerspectiveAMessages(prev => [...prev, navigatorMsg]);
      } else {
        setPerspectiveBMessages(prev => [...prev, navigatorMsg]);
      }
      
      // Small delay before other side responds
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 800));
      
      // === GENERATE AI's AUTONOMOUS RESPONSE AS THE OTHER PERSPECTIVE ===
      const aiAsOtherResponse = await generateOtherPerspectiveResponse(
        selectedScenario,
        currentPerspective,
        currentOtherMessages,
        otherPerspective
      );
      
      const aiAsOtherMsgId = Date.now() + 2;
      const aiAsOtherMsg = {
        id: aiAsOtherMsgId,
        role: 'self',
        content: aiAsOtherResponse,
        timestamp: new Date()
      };
      
      // Analyze for resident activation
      const otherActivations = analyzeForResidents(aiAsOtherResponse);
      
      // Update other messages with new response
      const updatedOtherMessages = [...currentOtherMessages, aiAsOtherMsg];
      
      // Add AI-as-other's message to OTHER panel
      if (isPlayingA) {
        setPerspectiveBMessages(prev => [...prev, aiAsOtherMsg]);
        setPerspectiveBActivations(otherActivations);
      } else {
        setPerspectiveAMessages(prev => [...prev, aiAsOtherMsg]);
        setPerspectiveAActivations(otherActivations);
      }
      
      // Small delay before Navigator responds to other side
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 800));
      
      // === GENERATE NAVIGATOR RESPONSE TO AI's MESSAGE ===
      const navigatorResponseToAI = await generateNavigatorResponse(
        selectedScenario,
        otherPerspective,
        updatedOtherMessages
      );
      
      const navigatorToAIMsgId = Date.now() + 3;
      const navigatorToAIMsg = {
        id: navigatorToAIMsgId,
        role: 'ai',
        content: navigatorResponseToAI,
        timestamp: new Date()
      };
      
      // Add Navigator response to OTHER panel
      if (isPlayingA) {
        setPerspectiveBMessages(prev => [...prev, navigatorToAIMsg]);
      } else {
        setPerspectiveAMessages(prev => [...prev, navigatorToAIMsg]);
      }
      
    } catch (error) {
      console.error('AI response error:', error);
      // Add fallback message if API fails
      const fallbackMsg = {
        id: Date.now() + 99,
        role: 'ai',
        content: "Tell me more about what's happening for you.",
        timestamp: new Date()
      };
      if (isPlayingA) {
        setPerspectiveAMessages(prev => [...prev, fallbackMsg]);
      } else {
        setPerspectiveBMessages(prev => [...prev, fallbackMsg]);
      }
    } finally {
      setAiIsTyping(false);
    }
  };
  
  const switchPerspective = () => {
    setCurrentPerspective(prev => prev === 'a' ? 'b' : 'a');
  };
  
  // The player commits first. Only then is the other side asked, in character, for its
  // own stance — and it is not told what the player chose.
  const declareOutcome = async (outcome) => {
    setGameOutcome(outcome);
    const isPlayingA = currentPerspective === 'a';
    const otherPerspective = isPlayingA
      ? selectedScenario?.perspectives[1]
      : selectedScenario?.perspectives[0];
    const otherMessages = isPlayingA ? perspectiveBMessages : perspectiveAMessages;

    setStancePending(true);
    let stance = null;
    try {
      stance = await generateOtherPerspectiveStance(
        selectedScenario,
        currentPerspective,
        otherMessages,
        otherPerspective
      );
    } catch (error) {
      console.error('Stance error:', error);
    }
    setAiStance(stance);
    setStancePending(false);
    setShowReveal(true);
    setRevealStage(1);
  };
  
  // The ending is the pair, not the player's half of it. With no stance to compare
  // against, it falls back to the player's call and the two-ending game.
  const endingType = !gameOutcome
    ? null
    : (aiStance && aiStance.stance !== gameOutcome) ? 'mismatch' : gameOutcome;
  
  const advanceReveal = () => {
    if (revealStage < 6) {
      setRevealStage(prev => prev + 1);
    } else {
      // Stage 6 carries its own exit; nothing should reach here.
      setShowReveal(false);
      navigateTo('home');
    }
  };
  
  const skipReveal = () => {
    setShowReveal(false);
    navigateTo('home');
  };
  
  // ============================================
  // RENDER: PRACTICE MODE V2 SPLIT SCREEN
  // ============================================
  
  const renderPracticeV2 = () => {
    const perspectiveA = selectedScenario?.perspectives[0];
    const perspectiveB = selectedScenario?.perspectives[1];
    const isPlayingA = currentPerspective === 'a';
    
    return (
      <div className="screen practice-v2-screen">
        {/* Header */}
        <header className="practice-v2-header">
          <button onClick={goBack} className="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="header-center">
            <span className="practice-badge">PRACTICE MODE</span>
            <h2>{selectedScenario?.title}</h2>
          </div>
          <div className="exchange-counter">
            <span>{totalExchanges}</span>
            <small>exchanges</small>
          </div>
        </header>
        
        {/* Matter Banner */}
        <div className="matter-banner">
          <span className="matter-label">THE MATTER</span>
          <p>{selectedScenario?.matter}</p>
        </div>
        
        {/* Split Screen Container */}
        <div className="split-screen-container">
          {/* Perspective A Panel */}
          <div className={`perspective-panel ${isPlayingA ? 'active' : 'watching'}`}>
            <div className="panel-header">
              <div className="panel-identity">
                <span className="perspective-letter">A</span>
                <div className="panel-info">
                  <strong>{perspectiveA?.name}</strong>
                  <span>{perspectiveA?.description}</span>
                </div>
              </div>
              <div className={`control-badge ${isPlayingA ? 'you' : 'ai'}`}>
                {isPlayingA ? 'YOU' : 'AI'}
              </div>
            </div>
            
            {/* Residents Display */}
            <div className="panel-residents">
              {RESIDENTS.map(r => (
                <div 
                  key={r.id} 
                  className={`resident-cell ${(perspectiveAActivations[r.id] || 0) > 2 ? 'active' : ''}`}
                >
                  {renderResidentIcon(r.id, 18)}
                  <div className="activation-bar">
                    <div 
                      className="activation-fill" 
                      style={{ width: `${(perspectiveAActivations[r.id] || 1) * 20}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Messages */}
            <div className="panel-messages">
              {perspectiveAMessages.map(msg => (
                <div 
                  key={msg.id} 
                  className={`message ${msg.role === 'ai' ? 'ai' : msg.role === 'self' ? 'self' : 'other'}`}
                >
                  <div className="message-content">{msg.content}</div>
                </div>
              ))}
              {aiIsTyping && !isPlayingA && (
                <div className="message ai typing">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Center Swap Button */}
          <div className="swap-divider">
            <button onClick={switchPerspective} className="swap-btn">
              <ArrowLeftRight size={20} />
              <span>SWAP</span>
            </button>
          </div>
          
          {/* Perspective B Panel */}
          <div className={`perspective-panel ${!isPlayingA ? 'active' : 'watching'}`}>
            <div className="panel-header">
              <div className="panel-identity">
                <span className="perspective-letter">B</span>
                <div className="panel-info">
                  <strong>{perspectiveB?.name}</strong>
                  <span>{perspectiveB?.description}</span>
                </div>
              </div>
              <div className={`control-badge ${!isPlayingA ? 'you' : 'ai'}`}>
                {!isPlayingA ? 'YOU' : 'AI'}
              </div>
            </div>
            
            {/* Residents Display */}
            <div className="panel-residents">
              {RESIDENTS.map(r => (
                <div 
                  key={r.id} 
                  className={`resident-cell ${(perspectiveBActivations[r.id] || 0) > 2 ? 'active' : ''}`}
                >
                  {renderResidentIcon(r.id, 18)}
                  <div className="activation-bar">
                    <div 
                      className="activation-fill" 
                      style={{ width: `${(perspectiveBActivations[r.id] || 1) * 20}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Messages */}
            <div className="panel-messages">
              {perspectiveBMessages.map(msg => (
                <div 
                  key={msg.id} 
                  className={`message ${msg.role === 'ai' ? 'ai' : msg.role === 'self' ? 'self' : 'other'}`}
                >
                  <div className="message-content">{msg.content}</div>
                </div>
              ))}
              {aiIsTyping && isPlayingA && (
                <div className="message ai typing">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Input Area */}
        <div className="practice-input-area">
          <div className="current-perspective-indicator">
            Speaking as: <strong>{isPlayingA ? perspectiveA?.name : perspectiveB?.name}</strong>
          </div>
          <div className="input-row">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePracticeMessage(inputMessage)}
              placeholder={`What would ${isPlayingA ? perspectiveA?.name : perspectiveB?.name} say?`}
              disabled={aiIsTyping}
            />
            <button 
              onClick={() => handlePracticeMessage(inputMessage)}
              disabled={!inputMessage.trim() || aiIsTyping}
              className="send-btn"
            >
              <Send size={20} />
            </button>
          </div>
          
          {/* Outcome Buttons - Only appear after 6+ exchanges */}
          {totalExchanges >= 6 && !gameOutcome && (
            <div className="outcome-prompt">
              <p>Do you see where this is going?</p>
              <div className="outcome-buttons">
                <button onClick={() => declareOutcome('bridge')} className="bridge-btn">
                  <GitBranch size={18} />
                  Bridge — We found alignment
                </button>
                <button onClick={() => declareOutcome('fork')} className="fork-btn">
                  <GitBranch size={18} style={{ transform: 'rotate(180deg)' }} />
                  Fork — Our paths diverge
                </button>
              </div>
              <p className="outcome-note">Both are valid outcomes. Neither is failure.</p>
              <p className="outcome-note">
                You call it first. {isPlayingA ? perspectiveB?.name : perspectiveA?.name} is asked
                after, and answers for themselves.
              </p>
            </div>
          )}
        </div>
        
        {/* Asking the other side, after the player has committed */}
        {stancePending && (
          <div className="reveal-overlay">
            <div className="reveal-container">
              <div className="reveal-stage">
                <p className="waiting-text">
                  You called it. Asking {isPlayingA ? perspectiveB?.name : perspectiveA?.name}…
                </p>
                <div className="waiting-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* The Reveal Modal */}
        {showReveal && renderRevealCeremony()}
      </div>
    );
  };
  
  // ============================================
  // RENDER: THE REVEAL CEREMONY (6 STAGES)
  // ============================================
  
  const renderRevealCeremony = () => {
    const perspectiveA = selectedScenario?.perspectives[0];
    const perspectiveB = selectedScenario?.perspectives[1];
    
    return (
      <div className="reveal-overlay">
        <div className="reveal-container">
          {/* Stage 1: The Consent */}
          {revealStage === 1 && (
            <div className="reveal-stage consent-stage">
              <div className="reveal-glow"></div>
              <h2>You navigated together.</h2>
              <p className="reveal-subtitle">
                {endingType === 'bridge'
                  ? 'A bridge exists.'
                  : endingType === 'fork'
                  ? 'An honest fork was accepted.'
                  : 'One of you still saw a bridge.'}
              </p>
              {aiStance && (
                <div className={`stance-reveal ${endingType === 'mismatch' ? 'mismatch' : 'matched'}`}>
                  <div className="stance-calls">
                    <div className="stance-side">
                      <span className="stance-who">You</span>
                      <span className="stance-call">{gameOutcome === 'bridge' ? 'Bridge' : 'Fork'}</span>
                    </div>
                    <div className="stance-side">
                      <span className="stance-who">{aiStance.name}</span>
                      <span className="stance-call">{aiStance.stance === 'bridge' ? 'Bridge' : 'Fork'}</span>
                    </div>
                  </div>
                  <p className="stance-reason">“{aiStance.reason}”</p>
                  <p className="stance-attrib">— {aiStance.name}</p>
                </div>
              )}
              <p className="reveal-prompt">There's one more step, if you're willing.</p>
              <div className="consent-buttons">
                <button onClick={advanceReveal} className="consent-yes">
                  I'm willing.
                </button>
                <button onClick={skipReveal} className="consent-no">
                  Not this time.
                </button>
              </div>
              <p className="consent-note">
                Only those who choose will see. Only those who choose will be seen.
              </p>
            </div>
          )}
          
          {/* Stage 2: The Waiting */}
          {revealStage === 2 && (
            <div className="reveal-stage waiting-stage">
              <div className="reveal-glow pulse"></div>
              <div className="waiting-symbols">
                {RESIDENTS.map((r, i) => (
                  <div 
                    key={r.id} 
                    className="waiting-symbol"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    {renderResidentIcon(r.id, 28)}
                  </div>
                ))}
              </div>
              <p className="waiting-text">Preparing to reveal...</p>
              <div className="waiting-dots">
                <span></span><span></span><span></span>
              </div>
              <button onClick={advanceReveal} className="continue-btn">
                Continue
              </button>
            </div>
          )}
          
          {/* Stage 3: The Unveiling */}
          {revealStage === 3 && (
            <div className="reveal-stage unveiling-stage">
              <div className="reveal-flash"></div>
              <div className="unveiling-houses">
                <div className="house-reveal left">
                  <div className="house-symbols">
                    {RESIDENTS.map((r, i) => (
                      <div 
                        key={r.id} 
                        className={`house-symbol ${(perspectiveAActivations[r.id] || 0) > 2 ? 'lit' : ''}`}
                        style={{ animationDelay: `${i * 0.15}s` }}
                      >
                        {renderResidentIcon(r.id, 24)}
                      </div>
                    ))}
                  </div>
                  <p className="house-name">{perspectiveA?.name}</p>
                </div>
                <div className="house-reveal right">
                  <div className="house-symbols">
                    {RESIDENTS.map((r, i) => (
                      <div 
                        key={r.id} 
                        className={`house-symbol ${(perspectiveBActivations[r.id] || 0) > 2 ? 'lit' : ''}`}
                        style={{ animationDelay: `${i * 0.15 + 0.5}s` }}
                      >
                        {renderResidentIcon(r.id, 24)}
                      </div>
                    ))}
                  </div>
                  <p className="house-name">{perspectiveB?.name}</p>
                </div>
              </div>
              <button onClick={advanceReveal} className="continue-btn">
                See More
              </button>
            </div>
          )}
          
          {/* Stage 4: The Seeing */}
          {revealStage === 4 && (
            <div className="reveal-stage seeing-stage">
              <h3>What Was Happening Inside</h3>
              <div className="seeing-panels">
                <div className="seeing-panel">
                  <h4>{perspectiveA?.name}</h4>
                  <div className="resident-insights">
                    {RESIDENTS.filter(r => (perspectiveAActivations[r.id] || 0) > 2).map(r => (
                      <div key={r.id} className="insight-item">
                        {renderResidentIcon(r.id, 20)}
                        <span className="resident-name">{r.name}</span>
                        <span className="resident-question">{r.question}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="seeing-panel">
                  <h4>{perspectiveB?.name}</h4>
                  <div className="resident-insights">
                    {RESIDENTS.filter(r => (perspectiveBActivations[r.id] || 0) > 2).map(r => (
                      <div key={r.id} className="insight-item">
                        {renderResidentIcon(r.id, 20)}
                        <span className="resident-name">{r.name}</span>
                        <span className="resident-question">{r.question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={advanceReveal} className="continue-btn">
                Continue
              </button>
            </div>
          )}
          
          {/* Stage 5: Where Houses Met */}
          {revealStage === 5 && (
            <div className="reveal-stage meeting-stage">
              <h3>Where Your Houses Met</h3>
              <div className="meeting-visual">
                <div className="house-a">
                  {RESIDENTS.filter(r => (perspectiveAActivations[r.id] || 0) > 2).slice(0, 3).map(r => (
                    <div key={r.id} className="meeting-resident">{renderResidentIcon(r.id, 24)}</div>
                  ))}
                </div>
                <div className={`meeting-bridge ${endingType}`}>
                  {endingType === 'bridge' ? (
                    <GitBranch size={40} />
                  ) : endingType === 'fork' ? (
                    <GitBranch size={40} style={{ transform: 'rotate(180deg)' }} />
                  ) : (
                    <ArrowLeftRight size={40} />
                  )}
                </div>
                <div className="house-b">
                  {RESIDENTS.filter(r => (perspectiveBActivations[r.id] || 0) > 2).slice(0, 3).map(r => (
                    <div key={r.id} className="meeting-resident">{renderResidentIcon(r.id, 24)}</div>
                  ))}
                </div>
              </div>
              <p className="meeting-message">
                {endingType === 'bridge'
                  ? "Different minds. Different fears. Same direction found."
                  : endingType === 'fork'
                  ? "Different minds. Different paths. Honest acknowledgment."
                  : "Different minds. Different readings of the same ground."}
              </p>
              <p className="meeting-note">That's rare. That's real.</p>
              <button onClick={advanceReveal} className="continue-btn">
                Complete The Reveal
              </button>
            </div>
          )}
          
          {/* Stage 6: The Close */}
          {revealStage === 6 && (
            <div className="reveal-stage close-stage">
              <div className="close-glow"></div>
              <div className="close-star">✧</div>
              <h2>You saw each other.</h2>
              <h2>You were seen.</h2>
              <div className="close-divider"></div>
              <p className="close-label">REVEAL COMPLETE</p>
              {/* The score is a function of exchanges only: a mismatch scores exactly
                  what a bridge or a fork scores. Do not weight it by endingType. */}
              <div className="score-reveal">
                <p>Score: {totalExchanges * 50} pts</p>
                <p>Reveal bonus: ×2</p>
                <p className="final-score">FINAL: {totalExchanges * 100} pts</p>
              </div>
              <div className="close-invite">
                <p>"This was something. Bring someone you trust."</p>
                <button className="invite-btn">
                  Invite to NMAVillage
                </button>
              </div>
              <button onClick={() => { setShowReveal(false); navigateTo('home'); }} className="home-btn">
                Home
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // ============================================
  // ORIGINAL SCREENS (Landing, Home, etc.)
  // ============================================
  
  // Landing Screen
  const renderLanding = () => (
    <div className="screen landing-screen" style={{position:"relative"}}>
      <div style={{position:"absolute",top:"16px",right:"16px"}}><a href="https://navigationalmind.com" style={{textDecoration:"none",color:"#94a3b8",display:"flex",alignItems:"center",gap:"4px",fontSize:"10px",fontFamily:"monospace",letterSpacing:"1.5px",textTransform:"uppercase",border:"1px solid rgba(100,116,139,0.1)",borderRadius:"4px",padding:"6px 12px"}}><ArrowLeft size={14} />Return</a></div><div className="landing-content">
        <div className="landing-symbols">
          {RESIDENTS.slice(0, 7).map((r, i) => (
            <div key={r.id} className="landing-symbol" style={{ animationDelay: `${i * 0.1}s` }}>
              {renderResidentIcon(r.id, 28)}
            </div>
          ))}
        </div>
        
        <h1>NMAVillage</h1>
        <p className="tagline">Multiple minds. One direction.</p>
        
        <div className="landing-buttons">
          <button onClick={() => setShowAuth(true)} className="primary-btn">
            Enter the Village
          </button>
          <button onClick={() => { setShowAuth(true); setAuthMode('signup'); }} className="secondary-btn">
            Create Account
          </button>
        </div>
        
        <p className="landing-subtitle">A game for navigating what matters.</p>
      </div>
    </div>
  );
  
  // Home Screen
  const renderHome = () => (
    <div className="screen home-screen">
      <header className="app-header">
        <div className="header-left">
          <div className="header-symbols">
            {RESIDENTS.slice(0, 3).map(r => (
              <span key={r.id}>{renderResidentIcon(r.id, 20)}</span>
            ))}
          </div>
          <div className="header-titles">
            <h1>NMAVillage</h1>
            <span className="header-subtitle">{getNavigatorClass(userProfile?.totalScore || 0).name}</span>
          </div>
        </div>
        <div className="header-right"><a href="https://navigationalmind.com" style={{textDecoration:"none",color:"#94a3b8",display:"flex",alignItems:"center",gap:"4px",fontSize:"10px",fontFamily:"monospace",letterSpacing:"1.5px",textTransform:"uppercase",border:"1px solid rgba(100,116,139,0.1)",borderRadius:"4px",padding:"6px 12px"}}><ArrowLeft size={14} />Return</a>
          <button onClick={() => navigateTo('profile')} className="header-btn">
            <User size={20} />
          </button>
        </div>
      </header>
      
      <div className="home-content">
        <div className="home-stats">
          <div className="stat-card">
            <span className="stat-value">{userProfile?.gamesPlayed || 0}</span>
            <span className="stat-label">Games</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{userProfile?.bridges || 0}</span>
            <span className="stat-label">Bridges</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{userProfile?.totalScore || 0}</span>
            <span className="stat-label">Score</span>
          </div>
        </div>
        
        <div className="home-actions">
          <div onClick={() => navigateTo('create-room')} className="action-card">
            <Plus size={32} />
            <span>Create Room</span>
            <p>Start a new navigation</p>
          </div>
          
          <div onClick={() => navigateTo('join-room')} className="action-card">
            <Users size={32} />
            <span>Join Room</span>
            <p>Enter with a code</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Create Room Screen
  const renderCreateRoom = () => (
    <div className="screen create-room-screen">
      <header className="screen-header">
        <button onClick={goBack} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h2>Create Room</h2>
      </header>
      
      {!roomMode ? (
        <div className="room-mode-select">
          <div onClick={() => setRoomMode('practice')} className="mode-card">
            <Sparkles size={32} />
            <h3>Practice Mode</h3>
            <p>Try scenarios. Learn the game. AI plays the other perspective.</p>
          </div>
          
          <div onClick={() => setRoomMode('real')} className="mode-card">
            <Heart size={32} />
            <h3>Real Mode</h3>
            <p>Navigate an actual matter with real people.</p>
          </div>
        </div>
      ) : roomMode === 'practice' ? (
        <div className="create-practice-room">
          <h3>Choose a Scenario</h3>
          <p className="scenario-count">{PRACTICE_SCENARIOS.length} scenarios across 10 life domains</p>
          
          {/* Category Filter */}
          <div className="category-filter">
            {['All', 'Relationships', 'Family', 'Work', 'Parenting', 'Friendship', 'Health', 'Money', 'Identity', 'Life Transitions', 'Values'].map(cat => (
              <button
                key={cat}
                onClick={() => setScenarioFilter(cat)}
                className={`filter-btn ${scenarioFilter === cat ? 'active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          <div className="scenario-list">
            {PRACTICE_SCENARIOS
              .filter(s => scenarioFilter === 'All' || s.category === scenarioFilter)
              .map(scenario => (
              <div 
                key={scenario.id}
                onClick={() => {
                  setPreviewScenario(scenario);
                  setShowScenarioPreview(true);
                }}
                className={`scenario-card ${selectedScenario?.id === scenario.id ? 'selected' : ''}`}
              >
                <span className="scenario-category">{scenario.category}</span>
                <h4>{scenario.title}</h4>
                <p>{scenario.matter}</p>
              </div>
            ))}
          </div>
          
          {/* Scenario Preview Modal */}
          {showScenarioPreview && previewScenario && (
            <div className="modal-overlay" onClick={() => setShowScenarioPreview(false)}>
              <div className="scenario-preview-modal" onClick={e => e.stopPropagation()}>
                <button className="close-btn" onClick={() => setShowScenarioPreview(false)}>×</button>
                
                <span className="preview-category">{previewScenario.category}</span>
                <h2>{previewScenario.title}</h2>
                
                <div className="preview-summary">
                  <div className="summary-item">
                    <span className="summary-label">WHAT</span>
                    <p>{previewScenario.summary?.what || previewScenario.matter}</p>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">HOW</span>
                    <p>{previewScenario.summary?.how || 'A situation that requires navigation.'}</p>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">WHERE</span>
                    <p>{previewScenario.summary?.where || 'The stakes are real.'}</p>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">WHO</span>
                    <p>{previewScenario.summary?.who || `${previewScenario.perspectives[0].name} and ${previewScenario.perspectives[1].name}`}</p>
                  </div>
                </div>
                
                <div className="preview-perspectives">
                  <h4>The Two Perspectives</h4>
                  <div className="perspective-preview-cards">
                    {previewScenario.perspectives.map(p => (
                      <div key={p.id} className="perspective-preview-card">
                        <strong>{p.name}</strong>
                        <span>{p.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <button 
                  className="primary-btn choose-scenario-btn"
                  onClick={() => {
                    setSelectedScenario(previewScenario);
                    setShowScenarioPreview(false);
                  }}
                >
                  Choose This Scenario
                </button>
              </div>
            </div>
          )}
          
          {selectedScenario && !showScenarioPreview && (
            <div className="perspective-select">
              <div className="selected-scenario-banner">
                <span>Selected:</span>
                <strong>{selectedScenario.title}</strong>
                <button onClick={() => setSelectedScenario(null)} className="change-btn">Change</button>
              </div>
              
              <h4>Start as which perspective?</h4>
              <div className="perspective-options">
                {selectedScenario.perspectives.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPerspective(p.id)}
                    className={`perspective-card ${selectedPerspective === p.id ? 'selected' : ''}`}
                  >
                    <strong>{p.name}</strong>
                    <span>{p.description}</span>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={startPracticeNavigation}
                disabled={!selectedPerspective}
                className="primary-btn"
              >
                Start Practice
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="create-real-room">
          <h3>What's the matter?</h3>
          <textarea
            value={roomMatter}
            onChange={(e) => setRoomMatter(e.target.value)}
            placeholder="Describe the shared situation you want to navigate..."
            className="matter-input"
          />
          <button 
            onClick={() => {/* Create real room logic */}}
            disabled={!roomMatter.trim()}
            className="primary-btn"
          >
            Create Room
          </button>
        </div>
      )}
    </div>
  );
  
  // Join Room Screen
  const renderJoinRoom = () => (
    <div className="screen join-room-screen">
      <header className="screen-header">
        <button onClick={goBack} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h2>Join Room</h2>
      </header>
      
      <div className="join-content">
        <h3>Enter Room Code</h3>
        <input
          type="text"
          value={roomCode}
          onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
          placeholder="XXXXX"
          maxLength={5}
          className="room-code-input"
        />
        <button 
          onClick={() => {/* Join room logic */}}
          disabled={roomCode.length !== 5}
          className="primary-btn"
        >
          Join
        </button>
      </div>
    </div>
  );
  
  // Resident Creation Screen
  const renderResidentCreation = () => {
    const currentResident = RESIDENTS[creationStep];
    
    // Skip button handler - use defaults for remaining residents
    const handleSkipCreation = async () => {
      const residents = {};
      RESIDENTS.forEach((r, i) => {
        residents[r.id] = {
          symbol: selectedSymbols[r.id] || r.symbols[0].id,
          color: selectedColors[r.id] || COLORS[i % COLORS.length].id
        };
      });
      
      const profile = {
        displayName: displayName || user?.email?.split('@')[0] || 'Navigator',
        residents,
        totalScore: 0,
        gamesPlayed: 0,
        bridges: 0,
        forks: 0,
        reveals: 0,
        createdAt: serverTimestamp()
      };
      
      await setDoc(doc(db, 'users', user.uid), profile);
      setUserProfile(profile);
      setCurrentScreen('home');
    };
    
    if (creationStep >= RESIDENTS.length) {
      return (
        <div className="screen resident-creation-screen">
          <header className="creation-header">
            <button onClick={() => setCreationStep(creationStep - 1)} className="back-btn-light">
              <ArrowLeft size={24} />
            </button>
            <button onClick={handleSkipCreation} className="skip-btn">
              Skip
            </button>
          </header>
          <div className="creation-content">
            <h2>One last thing</h2>
            <p>What should we call you?</p>
            
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              className="name-input"
            />
            
            <button
              onClick={handleResidentCreation}
              disabled={!displayName.trim()}
              className="primary-btn"
            >
              Enter the Village
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="screen resident-creation-screen">
        <header className="creation-header">
          {creationStep > 0 ? (
            <button onClick={() => setCreationStep(creationStep - 1)} className="back-btn-light">
              <ArrowLeft size={24} />
            </button>
          ) : (
            <button onClick={() => { handleSignOut(); }} className="back-btn-light">
              <ArrowLeft size={24} />
            </button>
          )}
          <button onClick={handleSkipCreation} className="skip-btn">
            Skip All
          </button>
        </header>
        
        <div className="creation-progress">
          {RESIDENTS.map((_, i) => (
            <div key={i} className={`progress-dot ${i <= creationStep ? 'active' : ''}`} />
          ))}
        </div>
        
        <div className="creation-content">
          <h2>{currentResident.name}</h2>
          <p className="resident-question-display">"{currentResident.question}"</p>
          <p className="resident-description">{currentResident.description}</p>
          
          <div className="symbol-select">
            <h4>Choose a symbol</h4>
            <div className="symbol-options">
              {currentResident.symbols.map(symbol => {
                const IconComponent = symbol.icon;
                return (
                  <button
                    key={symbol.id}
                    onClick={() => setSelectedSymbols({ ...selectedSymbols, [currentResident.id]: symbol.id })}
                    className={`symbol-btn ${selectedSymbols[currentResident.id] === symbol.id ? 'selected' : ''}`}
                  >
                    <IconComponent size={32} />
                    <span>{symbol.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          <div className="color-select">
            <h4>Choose a color</h4>
            <div className="color-options">
              {COLORS.map(color => (
                <button
                  key={color.id}
                  onClick={() => setSelectedColors({ ...selectedColors, [currentResident.id]: color.id })}
                  className={`color-btn ${selectedColors[currentResident.id] === color.id ? 'selected' : ''}`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
          
          <button
            onClick={() => setCreationStep(creationStep + 1)}
            className="primary-btn"
          >
            {creationStep < RESIDENTS.length - 1 ? 'Next' : 'Finish'}
          </button>
        </div>
      </div>
    );
  };
  
  // Profile Screen
  const renderProfile = () => (
    <div className="screen profile-screen">
      <header className="screen-header">
        <button onClick={goBack} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h2>Profile</h2>
      </header>
      
      <div className="profile-content">
        <div className="profile-header">
          <div className="profile-symbols">
            {RESIDENTS.map(r => renderResidentIcon(r.id, 24))}
          </div>
          <h2>{userProfile?.displayName}</h2>
          <p className="profile-class">{getNavigatorClass(userProfile?.totalScore || 0).name}</p>
        </div>
        
        <div className="profile-stats">
          <h3>STATS</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-value">{userProfile?.gamesPlayed || 0}</span>
              <span className="stat-label">Games Played</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{userProfile?.bridges || 0}</span>
              <span className="stat-label">Bridges Built</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{userProfile?.forks || 0}</span>
              <span className="stat-label">Forks Accepted</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{userProfile?.reveals || 0}</span>
              <span className="stat-label">Reveals Given</span>
            </div>
          </div>
        </div>
        
        <button onClick={handleSignOut} className="secondary-btn signout-btn">
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
  
  // Auth Modal
  const renderAuthModal = () => (
    <div className="modal-overlay" onClick={() => setShowAuth(false)}>
      <div className="modal-content auth-modal" onClick={e => e.stopPropagation()}>
        <h2>{authMode === 'login' ? 'Sign In' : 'Create Account'}</h2>
        
        {authError && <p className="auth-error">{authError}</p>}
        
        <form onSubmit={handleEmailAuth}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="primary-btn">
            {authMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>
        
        {authMode === 'login' && (
          <p className="forgot-password">
            <button onClick={handlePasswordReset}>Forgot password?</button>
          </p>
        )}
        
        <div className="auth-divider">or</div>
        
        <button onClick={handleGoogleAuth} className="google-btn">
          Continue with Google
        </button>
        
        <button onClick={handleGuestMode} className="guest-btn">
          Continue as Guest
        </button>
        
        <p className="auth-switch">
          {authMode === 'login' ? (
            <>Don't have an account? <button onClick={() => setAuthMode('signup')}>Sign up</button></>
          ) : (
            <>Already have an account? <button onClick={() => setAuthMode('login')}>Sign in</button></>
          )}
        </p>
      </div>
    </div>
  );
  
  // ============================================
  // MAIN RENDER
  // ============================================
  
  if (authLoading) {
    return (
      <div className="app-container loading">
        <Loader2 className="spinner" size={48} />
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      {currentScreen === 'landing' && renderLanding()}
      {currentScreen === 'home' && renderHome()}
      {currentScreen === 'create-room' && renderCreateRoom()}
      {currentScreen === 'join-room' && renderJoinRoom()}
      {currentScreen === 'resident-creation' && renderResidentCreation()}
      {currentScreen === 'profile' && renderProfile()}
      
      {/* Practice Mode V2 */}
      {currentScreen === 'practice-v2' && renderPracticeV2()}
      
      {showAuth && renderAuthModal()}
    </div>
  );
}
